import { EDGES } from '@runtime/game/logic/edges'

import type {
  ThemePalette,
  ThemeTiles,
} from '@runtime/core/theme/themeEngineShared'

type GeneratedThemeColors = {
  top?: unknown
  right?: unknown
  bottom?: unknown
  left?: unknown
}

const HEX_COLOR_RE = /^#([a-f\d]{6})$/i

const DEFAULT_GENERATED_THEME_COLORS = {
  top: '#F4D35E',
  right: '#DE5F58',
  bottom: '#3D82D9',
  left: '#53A86B',
} as const

const POSTER_PUZZLE_GRID_SIZE = 5
const POSTER_PUZZLE_CANVAS_SIZE = 500
const POSTER_PUZZLE_TILE_SIZE =
  POSTER_PUZZLE_CANVAS_SIZE / POSTER_PUZZLE_GRID_SIZE
const GENERATED_THEME_TILE_KEYS = ['t1', 't2', 't3', 't4', 't5', 't6'] as const

export const GENERATED_THEME_BASE_PALETTE = {
  surface: '#191D24',
  surfaceVariant: '#272C36',
  text: '#E9EDF3',
  border: '#3B4356',
} as const

function parseHexColor(value: unknown): string | null {
  const raw = String(value || '').trim()

  if (/^#([a-f\d]{3})$/i.test(raw)) {
    const [, short] = /^#([a-f\d]{3})$/i.exec(raw) || []
    if (short) {
      return `#${short[0]}${short[0]}${short[1]}${short[1]}${short[2]}${short[2]}`.toUpperCase()
    }
  }

  if (!HEX_COLOR_RE.test(raw)) return null

  return raw.toUpperCase()
}

function normalizeHexColor(value: unknown, fallback: string) {
  return parseHexColor(value) || fallback.toUpperCase()
}

function normalizeGeneratedThemeColors(colors?: GeneratedThemeColors | null) {
  return {
    top: normalizeHexColor(colors?.top, DEFAULT_GENERATED_THEME_COLORS.top),
    right: normalizeHexColor(
      colors?.right,
      DEFAULT_GENERATED_THEME_COLORS.right,
    ),
    bottom: normalizeHexColor(
      colors?.bottom,
      DEFAULT_GENERATED_THEME_COLORS.bottom,
    ),
    left: normalizeHexColor(colors?.left, DEFAULT_GENERATED_THEME_COLORS.left),
  }
}

function normalizeCompleteGeneratedThemeColors(
  colors?: GeneratedThemeColors | null,
) {
  const top = parseHexColor(colors?.top)
  const right = parseHexColor(colors?.right)
  const bottom = parseHexColor(colors?.bottom)
  const left = parseHexColor(colors?.left)

  if (!(top && right && bottom && left)) return null

  return { top, right, bottom, left }
}

function normalizePlayerPalette(value: unknown) {
  if (!Array.isArray(value) || value.length < 4) return null

  const players = value.slice(0, 4).map((color) => parseHexColor(color))
  return players.every(Boolean) ? (players as string[]) : null
}

function buildTokenPlayerPalette(
  colors: ReturnType<typeof normalizeGeneratedThemeColors>,
) {
  return [colors.right, colors.bottom, colors.top, colors.left]
}

function buildSenwPlayerPalette(
  colors: ReturnType<typeof normalizeGeneratedThemeColors>,
) {
  return [colors.bottom, colors.right, colors.top, colors.left]
}

function escapeXmlAttribute(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function buildTileSvg(
  edgeTokens: readonly string[],
  colors: ReturnType<typeof normalizeGeneratedThemeColors>,
  logoUrl?: string | null,
) {
  const tokenToColor = {
    YEL: colors.top,
    RED: colors.right,
    BLU: colors.bottom,
    GRN: colors.left,
  } as const
  const [topToken, rightToken, bottomToken, leftToken] = edgeTokens
  const logoMarkup =
    logoUrl && logoUrl.trim()
      ? `<image href="${escapeXmlAttribute(logoUrl)}" x="34" y="34" width="32" height="32" preserveAspectRatio="xMidYMid meet" />`
      : ''

  return `<svg xmlns="http://www.w3.org/2000/svg" width="591" height="591" viewBox="0 0 100 100">
  <polygon points="0,0 100,0 50,50" fill="${tokenToColor[topToken as keyof typeof tokenToColor] || colors.top}" />
  <polygon points="100,0 100,100 50,50" fill="${tokenToColor[rightToken as keyof typeof tokenToColor] || colors.right}" />
  <polygon points="100,100 0,100 50,50" fill="${tokenToColor[bottomToken as keyof typeof tokenToColor] || colors.bottom}" />
  <polygon points="0,100 0,0 50,50" fill="${tokenToColor[leftToken as keyof typeof tokenToColor] || colors.left}" />
  ${logoMarkup}
</svg>`
}

function toSvgDataUrl(svg: string) {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

function buildPosterPuzzleArtwork(
  colors: ReturnType<typeof normalizeGeneratedThemeColors>,
) {
  return `
    <defs>
      <linearGradient id="poster-bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#111827" />
        <stop offset="100%" stop-color="#273046" />
      </linearGradient>
      <clipPath id="poster-clip">
        <rect x="16" y="16" width="468" height="468" rx="28" ry="28" />
      </clipPath>
    </defs>
    <rect width="${POSTER_PUZZLE_CANVAS_SIZE}" height="${POSTER_PUZZLE_CANVAS_SIZE}" fill="#111827" />
    <rect
      x="16"
      y="16"
      width="468"
      height="468"
      rx="28"
      ry="28"
      fill="url(#poster-bg)"
    />
    <g clip-path="url(#poster-clip)">
      <rect
        x="-18"
        y="8"
        width="244"
        height="484"
        fill="${colors.top}"
        opacity="0.95"
        transform="rotate(-6 104 250)"
      />
      <circle cx="384" cy="122" r="138" fill="${colors.right}" opacity="0.92" />
      <circle cx="112" cy="406" r="166" fill="${colors.left}" opacity="0.82" />
      <path
        d="M30 274C165 164 314 152 472 36V212C315 320 184 330 30 444Z"
        fill="${colors.bottom}"
        opacity="0.9"
      />
      <path
        d="M0 326C120 260 252 248 500 172V500H0Z"
        fill="#191D24"
        opacity="0.28"
      />
      <g
        stroke="${GENERATED_THEME_BASE_PALETTE.text}"
        stroke-opacity="0.18"
        fill="none"
      >
        <path d="M32 110H468" />
        <path d="M32 390H468" />
        <path d="M250 32V468" />
      </g>
      <g fill="${GENERATED_THEME_BASE_PALETTE.text}">
        <text
          x="58"
          y="126"
          font-family="Inter,Arial"
          font-size="68"
          font-weight="900"
          letter-spacing="4"
        >
          UNITED
        </text>
        <text
          x="58"
          y="202"
          font-family="Inter,Arial"
          font-size="68"
          font-weight="900"
          letter-spacing="4"
        >
          SQUARE
        </text>
        <text
          x="58"
          y="248"
          font-family="Inter,Arial"
          font-size="24"
          font-weight="700"
          letter-spacing="10"
          fill-opacity="0.72"
        >
          ORIGINAL
        </text>
      </g>
      <g
        fill="none"
        stroke="${GENERATED_THEME_BASE_PALETTE.text}"
        stroke-opacity="0.55"
      >
        <circle cx="356" cy="320" r="118" stroke-width="20" />
        <circle cx="356" cy="320" r="78" stroke-width="6" />
      </g>
    </g>
    <rect
      x="16"
      y="16"
      width="468"
      height="468"
      rx="28"
      ry="28"
      fill="none"
      stroke="${GENERATED_THEME_BASE_PALETTE.text}"
      stroke-opacity="0.45"
      stroke-width="6"
    />
  `
}

export function buildGeneratedThemePalette(
  colors?: GeneratedThemeColors | null,
  existingPalette?: ThemePalette,
): ThemePalette {
  const completeColors = normalizeCompleteGeneratedThemeColors(colors)
  const normalizedColors =
    completeColors || normalizeGeneratedThemeColors(colors)
  const existingPlayers = normalizePlayerPalette(existingPalette?.players)
  const players =
    completeColors || !existingPlayers
      ? buildTokenPlayerPalette(normalizedColors)
      : existingPlayers

  return {
    ...existingPalette,
    players,
    ...(completeColors
      ? {
          playersSenw: buildSenwPlayerPalette(normalizedColors),
          cardinal: {
            south: normalizedColors.bottom,
            S: normalizedColors.bottom,
            east: normalizedColors.right,
            E: normalizedColors.right,
            north: normalizedColors.top,
            N: normalizedColors.top,
            west: normalizedColors.left,
            W: normalizedColors.left,
          },
        }
      : {}),
    primary: normalizeHexColor(
      existingPalette?.primary,
      normalizedColors.right,
    ),
    secondary: normalizeHexColor(
      existingPalette?.secondary,
      normalizedColors.top,
    ),
    surface: GENERATED_THEME_BASE_PALETTE.surface,
    surfaceVariant: GENERATED_THEME_BASE_PALETTE.surfaceVariant,
    text: GENERATED_THEME_BASE_PALETTE.text,
    border: GENERATED_THEME_BASE_PALETTE.border,
  }
}

export function buildGeneratedThemeTiles(
  colors?: GeneratedThemeColors | null,
  logoUrl?: string | null,
): ThemeTiles {
  const normalizedColors = normalizeGeneratedThemeColors(colors)

  return {
    center: toSvgDataUrl(buildTileSvg(EDGES.center, normalizedColors, logoUrl)),
    set: GENERATED_THEME_TILE_KEYS.map((key) =>
      toSvgDataUrl(buildTileSvg(EDGES[key], normalizedColors)),
    ),
  }
}

export function buildGeneratedThemePuzzlePosterTiles(
  colors?: GeneratedThemeColors | null,
): string[] {
  const normalizedColors = normalizeGeneratedThemeColors(colors)
  const artwork = buildPosterPuzzleArtwork(normalizedColors)

  return Array.from(
    { length: POSTER_PUZZLE_GRID_SIZE * POSTER_PUZZLE_GRID_SIZE },
    (_, index) => {
      const row = Math.floor(index / POSTER_PUZZLE_GRID_SIZE)
      const col = index % POSTER_PUZZLE_GRID_SIZE

      return toSvgDataUrl(
        `<svg xmlns="http://www.w3.org/2000/svg" width="591" height="591" viewBox="${col * POSTER_PUZZLE_TILE_SIZE} ${row * POSTER_PUZZLE_TILE_SIZE} ${POSTER_PUZZLE_TILE_SIZE} ${POSTER_PUZZLE_TILE_SIZE}">${artwork}</svg>`,
      )
    },
  )
}
